import { Icons } from '@/enums/payments'
import { MemberStatuses, NewMemberStatusesKeys } from '@/enums/member'
import { CountryCodes, Currencies, NewCountries } from '@/enums/general'
import config from '@/config/general'
import moment from 'moment'
import icons from '@/assets/icons'
import { datadogRum } from '@datadog/browser-rum'

/**
 * Capitalizes a word
 *
 * @param word
 * @returns {string}
 */
export const capitalize = (word = '') => {
  if (typeof word !== 'string' || word.length === 0) {
    return ''
  }

  const letters = word.split('')
  return letters[0].toUpperCase() + letters.slice(1).join('')
}

/**
 * Convert image object to the right format for the srcSet attribute
 *
 * @param image
 * @param s3
 * @returns {{desktop: string, mobile: string}}
 */
export const convertSrcSet = (image, s3 = false) => {
  const result = {
    desktop: '',
    mobile: '',
    src: '',
    alt: ''
  }

  const runtimeConfig = useRuntimeConfig()

  const basePath = s3 ? runtimeConfig.public.BASE_S3_PATH : ''

  if (!Object.prototype.hasOwnProperty.call(image, 'desktop') && !Object.prototype.hasOwnProperty.call(image, 'mobile')) {
    return result
  }

  const desktop = Object.prototype.hasOwnProperty.call(image, 'desktop') ? 'desktop' : 'mobile'
  const mobile = Object.prototype.hasOwnProperty.call(image, 'mobile') ? 'mobile' : 'desktop'

  return {
    desktop: `${basePath}${image[desktop]['1x']},  ${basePath}${image[desktop]['2x']} 2x`.toLowerCase(),
    mobile: `${basePath}${image[mobile]['1x']},  ${basePath}${image[mobile]['2x']} 2x`.toLowerCase(),
    src: `${basePath + image[mobile]['1x']}`.toLowerCase()
  }
}

/**
 * Convert a single source to the right format
 *
 * @param {string} path
 * @param {boolean} s3
 * @param {any} runtimeConfig - Optional runtime config (for SSR compatibility)
 * @returns {string}
 */
export const convertSrc = (path = '', s3 = false, runtimeConfig = null) => {
  // Use provided config or try to get it (may fail in SSR if called outside proper context)
  let config = runtimeConfig
  if (!config) {
    try {
      config = useRuntimeConfig()
    } catch (_e) {
      // useRuntimeConfig not available in this context (e.g., SSR outside proper context)
      config = null
    }
  }
  const basePath = s3 && config ? config.public.BASE_S3_PATH : ''

  return `${basePath}${path}`.toLowerCase()
}

/**
 * Formats address lines and returns array with strings for each line.
 *
 * @param addressObj
 * @returns {string[]}
 */

export const formatAddressLines = (addressObj) => {
  return [addressObj.street + ' ' + addressObj.houseNumber + addressObj.annex, addressObj.postalCode + ' ' + addressObj.city]
}

/**
 * Returns the string for a numeric member status.
 *
 * @param value
 * @returns {string}
 */
export const getMemberStatus = (value) => {
  let status = 'Inactive'

  switch (value) {
    case MemberStatuses.enabled:
    case NewMemberStatusesKeys.active:
      status = 'Active'
      break
    case MemberStatuses.new:
    case NewMemberStatusesKeys.trial_period:
      status = 'Trial'
      break
    case MemberStatuses.paused:
    case NewMemberStatusesKeys.paused:
      status = 'Paused'
      break
  }

  return status
}

/**
 * Returns the string for a numeric payment status.
 *
 * @param value
 * @returns {string}
 */
export const getPaymentStatus = (value) => {
  let status = 'Waste'

  switch (value) {
    case 0:
      status = 'Open'
      break
    case 1:
    case 7:
      status = 'Paid'
      break
    case 2:
      status = 'Failed'
      break
    case 3:
      status = 'ChargeBack'
      break
    case 4:
      status = 'LatePayment'
      break
    case 5:
      status = 'Pending'
      break
    case 6:
      status = 'Refund'
      break
  }

  return status
}

/**
 * Formats a string with kebab case to pascal case
 *
 * @param string
 * @returns {string[]}
 */
export const kebabToPascal = (string) => {
  return string
    .split('-')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join('')
}

/**
 * Returns a array with icons per payment method
 *
 * @param paymentMethod string
 * @returns {array[]}
 */
export const getPaymentIcon = (paymentMethod) => {
  return Icons[paymentMethod.toLowerCase()]
}

/**
 * Returns uppercase initials from fullname.
 *
 * @param fullName
 * @returns {*}
 */
export const getInitials = (fullName) => {
  const names = fullName.split(' ')

  const firstLetter = names[0].charAt(0)
  const lastLetter = names[names.length - 1].charAt(0)

  return (firstLetter + lastLetter).toUpperCase()
}

/**
 * Returns object with browser name and version.
 *
 * @returns {{name: string, version: string}}
 */
export const getBrowser = () => {
  const ua = navigator.userAgent
  let tem
  let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || []

  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || []
    return { name: 'IE', version: tem[1] || '' }
  }

  if (M[1] === 'Chrome') {
    tem = ua.match(/\bOPR|Edge\/(\d+)/)
    if (tem != null) {
      return { name: 'Opera', version: tem[1] }
    }
  }

  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?']

  if ((tem = ua.match(/version\/(\d+)/i)) != null) {
    M.splice(1, 1, tem[1])
  }

  return {
    name: M[0],
    version: M[1],
    isMobile: ua.includes('Mobile')
  }
}

/**
 * Toggle modal visibility.
 *
 * @param modal
 * @param open
 */
export const togglePageScroll = (open = true) => {
  if (import.meta.client) {
    try {
      const body = document.querySelector('body')
      if (open) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        body.style.paddingRight = `${scrollbarWidth}px`
        body.classList.add('overflow-hidden')
      } else {
        body.style.paddingRight = ''
        body.classList.remove('overflow-hidden')
      }
    } catch (_error) {
      // noop
    }
  }
}

/**
 * Format date to YYYY-MM-DD (H:i:s) string.
 *
 * @param date
 * @param includeTime
 * @returns {string}
 */
export const formatDateToString = (date, includeTime = false) => {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) {
    month = '0' + month
  }
  if (day.length < 2) {
    day = '0' + day
  }

  let dateString = [year, month, day].join('-')

  if (includeTime) {
    let hours = '' + d.getHours()
    let minutes = '' + d.getMinutes()
    let seconds = '' + d.getSeconds()

    if (hours.length < 2) {
      hours = '0' + hours
    }
    if (minutes.length < 2) {
      minutes = '0' + minutes
    }
    if (seconds.length < 2) {
      seconds = '0' + seconds
    }

    dateString = dateString + ' ' + [hours, minutes, seconds].join(':')
  }

  return dateString
}

/**
 * Adds local currency symbol
 *
 * @param amount
 * @param countryCode
 * @param currencyCode
 */
export const addLocalCurrency = (amount = 0, countryCode = '', currencyCode) => {
  if (countryCode.toLowerCase() === CountryCodes.denmark && currencyCode === 'DKK') {
    return `${amount} kr.`
  }

  if (countryCode.toLowerCase() === CountryCodes.norway && currencyCode === 'NOK') {
    return `${amount} kr`
  }

  if (countryCode.toLowerCase() === CountryCodes.sweden && currencyCode === 'SEK') {
    return `${amount}kr`
  }

  if (countryCode.toLowerCase() === CountryCodes.belgium && currencyCode === 'EUR') {
    return `€ ${amount}`
  }

  if (countryCode.toLowerCase() === CountryCodes.unitedKingdom) {
    return amount.toLocaleString('gb', { style: 'currency', currency: currencyCode }).replace(',', '.')
  }

  if (
    currencyCode === Currencies[CountryCodes.poland] ||
    currencyCode === Currencies[CountryCodes.norway] ||
    currencyCode === Currencies[CountryCodes.czechRepublic]
  ) {
    return amount.toLocaleString(countryCode, { style: 'currency', currency: currencyCode }).replace(',00', '')
  }

  const amountWithSpaces = amount.toLocaleString(countryCode, { style: 'currency', currency: currencyCode })

  if (currencyCode === Currencies[CountryCodes.switzerland]) {
    return amountWithSpaces
  }

  const amountWithoutSpaces = amountWithSpaces.replace(/\s/g, '')

  return amountWithoutSpaces
}

/*
 * Gets the hash value from the url
 */
export const getUrlHash = () => {
  if (import.meta.client) {
    let currentHash = window.location.hash

    if (currentHash.includes('?')) {
      currentHash = currentHash.split('?')[0]
    }

    return currentHash.replace('#', '')
  }

  return ''
}

/**
 * Adds a hash to the url
 *
 * @param hash
 */
export const setUrlHash = (hash) => {
  if (import.meta.client) {
    window.location.hash = hash
  }
}

/**
 * Logs error to bug tracking tool
 *
 * @param error
 */
export const logError = (error) => {
  console.log('logError', { error })
  if (import.meta.client && window) {
    datadogRum.addError(error)
  }
}

/**
 * gets the image paths
 *
 * @param path
 * @param s3
 */
export const getImagePaths = ({
  path = '',
  extension = '.webp',
  s3 = false,
  noDesktop = false,
  noMobile = false,
  mobilePostFix = '-mob',
  retinaPostFix = '@2x'
}) => {
  return convertSrcSet(
    {
      ...(noDesktop
        ? {}
        : {
            desktop: {
              '1x': `${path}${extension}`,
              '2x': `${path}${retinaPostFix}${extension}`
            }
          }),
      ...(noMobile
        ? {}
        : {
            mobile: {
              '1x': `${path}${mobilePostFix}${extension}`,
              '2x': `${path}${mobilePostFix}${retinaPostFix}${extension}`
            }
          })
    },
    s3
  )
}

/**
 * Gets custom error messages
 *
 * @param error
 */
export const getCustomErrorMessages = ({ graphQLErrors }) => {
  if (graphQLErrors && graphQLErrors.length) {
    const graphQLErrorObject = graphQLErrors.find((error) => Object.prototype.hasOwnProperty.call(error, 'messages'))

    if (graphQLErrorObject) {
      return graphQLErrorObject
    }
  }

  return {}
}

/**
 * Gets custom error messages
 *
 * @param error
 */
export const getRESTCustomErrorMessages = (violations) => {
  if (violations && violations.length) {
    return violations.reduce(
      (acc, { propertyPath, messageKey }) => ({
        ...acc,
        [propertyPath]: messageKey
      }),
      {}
    )
  }

  return null
}

/**
 * Gets custom error messages for V2 API
 *
 */
export const getRESTV2CustomErrorMessages = (error) => {
  let populatedErrors = null

  if (error && error.response?.data?.violations) {
    populatedErrors = error.response.data.violations.reduce(
      (acc, { propertyPath, messageKey, message, title, template }) => ({
        ...acc,
        [propertyPath]: {
          locizeKey: `Api.${messageKey || template}`,
          propertyPath,
          message: message || title
        }
      }),
      {}
    )
  }

  if (error && error.response?.data?.error && error.response?.data?.errorKey) {
    populatedErrors = {
      ...(populatedErrors || {}),
      general: {
        locizeKey: `Api.${error.response.data.errorKey}`,
        message: error.response.data.error,
        propertyPath: 'general'
      }
    }
  }

  return populatedErrors
}

/**
 * Formats a number
 * example 10000 -> 10.000
 *
 * @param number
 */
export const formatNumber = (number = 0) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

/*
 * Preloads media
 *
 * @param url
 */
export const preloadMedia = (url = '') => {
  if (url && import.meta.client) {
    const img = new Image()
    img.src = url
  }
}

/*
 * Modifies order bundles
 *
 * TODO: DELETE
 *
 * @param orderBundle
 * @param name
 * @param displayName
 */
export const modifyOrderBundle = (orderBundle, name, displayName) => {
  if (orderBundle.orders.edges.length) {
    const campaignId = orderBundle.orders.edges[0].node.campaign?._id
    const campaignIds = [59, 60]

    if (campaignIds.includes(campaignId)) {
      return {
        ...orderBundle,
        orders: {
          ...orderBundle.orders,
          edges: orderBundle.orders.edges.map((edge) => ({
            ...edge,
            node: {
              ...edge.node,
              product: {
                ...edge.node.product,
                productImages: config.unknownMenProduct,
                name,
                design: {
                  ...edge.node.product.design,
                  name
                },
                productGroupSize: {
                  ...edge.node.product.productGroupSize,
                  productGroup: {
                    ...edge.node.product.productGroupSize.productGroup,
                    displayName
                  }
                }
              }
            }
          }))
        }
      }
    }
  }

  return orderBundle
}

export const isDiscountCampaign = (country) => {
  const allowedCountries = [
    CountryCodes.unitedKingdom,
    CountryCodes.netherlands,
    CountryCodes.germany,
    CountryCodes.belgium,
    CountryCodes.austria,
    CountryCodes.france,
    CountryCodes.spain
  ]

  const showFromDate = moment('28-04-2022', 'DD-MM-YYYY')
  const showToDate = moment('04-05-2022', 'DD-MM-YYYY')

  const isDateToShowBanner = moment().isBetween(showFromDate, showToDate)

  return isDateToShowBanner && allowedCountries.includes(country)
}

export const isFreeSocksCampaign = (country) => {
  const allowedCountries = [
    CountryCodes.austria,
    CountryCodes.belgium,
    CountryCodes.denmark,
    CountryCodes.germany,
    CountryCodes.spain,
    CountryCodes.france,
    CountryCodes.netherlands,
    CountryCodes.unitedKingdom,
    CountryCodes.sweden
  ]

  const showFromDate = moment('28-04-2022', 'DD-MM-YYYY')
  const showToDate = moment('04-05-2043', 'DD-MM-YYYY')

  const isFreeSocksCampaign = moment().isBetween(showFromDate, showToDate)

  return isFreeSocksCampaign && allowedCountries.includes(country)
}

export const isSignUpCampaign = (country = 'nl') => {
  const allowedCountries = [
    CountryCodes.unitedKingdom,
    CountryCodes.netherlands,
    CountryCodes.germany,
    CountryCodes.belgium,
    CountryCodes.austria
  ]

  const showFromDate = moment('28-04-2022', 'DD-MM-YYYY')
  const showToDate = moment('04-05-2022', 'DD-MM-YYYY')
  const isDateToShowBanner = moment().isBetween(showFromDate, showToDate)

  return isDateToShowBanner && allowedCountries.includes(country)
}

export const getCorrectedDeliveryDates = (design = '', _) => {
  const designInLowerCase = design.toLowerCase()

  switch (designInLowerCase) {
    case 'heroes':
      return {
        fromDate: moment('15-09-2023', 'DD-MM-YYYY'),
        tillDate: moment('23-09-2023', 'DD-MM-YYYY')
      }
    case 'sixtie':
      return {
        fromDate: moment('17-05-2024', 'DD-MM-YYYY'),
        tillDate: moment('23-05-2024', 'DD-MM-YYYY')
      }
  }

  return null
}

export const getCorrectReturnAfterDay = (design = '', country) => {
  const designInLowerCase = design.toLowerCase()

  switch (designInLowerCase) {
    case 'beau':
      if ([CountryCodes.netherlands, CountryCodes.austria, CountryCodes.spain, CountryCodes.sweden].includes(country)) {
        return moment('19-06-2022', 'DD-MM-YYYY').format('LL')
      } else if ([CountryCodes.belgium, CountryCodes.germany].includes(country)) {
        return moment('17-06-2022', 'DD-MM-YYYY').format('LL')
      } else {
        return moment('18-06-2022', 'DD-MM-YYYY').format('LL')
      }
  }

  switch (country) {
    case CountryCodes.austria:
      return 'the 25th of this month'
    case CountryCodes.belgium:
      return 'de 20ste'
    case CountryCodes.denmark:
      return 'the 25th of this month'
    case CountryCodes.germany:
      return 'the 25th of this month'
    case CountryCodes.spain:
      return 'the 25th of this month'
    case CountryCodes.france:
      return 'the 25th of this month'
    case CountryCodes.netherlands:
      return 'de 20ste'
    case CountryCodes.unitedKingdom:
      return 'the 25th of this month'
    case CountryCodes.sweden:
      return 'the 25th of this month'
  }

  return moment(new Date()).add(14, 'day').format('LL')
}

export const serializeParameters = (obj = {}) => {
  const str = []
  const keys = Object.keys(obj)
  keys.forEach((key) => {
    if (obj[key]) {
      str.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    }
  })
  return str.join('&')
}

export const stripLanguageRegex = new RegExp('^\\/[a-z]{2}-[a-z]{2}/') // strips any language from the path (like: '/nl-nl/' or '/de-nl/')

export const objKeysToLowerCase = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value]))

export const showLoyaltyProgram = (country = '') => country || true

export const getEuros = (country = 'nl', amount = 0, keepZeros = false) => {
  const countryCode = country.toLowerCase()

  let euros = amount.toLocaleString(countryCode, { style: 'currency', currency: 'EUR' }).replace(/\s/g, '')

  if (countryCode === CountryCodes.unitedKingdom) {
    euros = euros.replace(',', '.')
  }

  if (!keepZeros) {
    euros = euros.replace(',00', '').replace('.00', '')
  }

  return euros
}

export const isFeaturedBoxersCampaign = () => {
  const showFromDate = moment('12-10-2023', 'DD-MM-YYYY')
  const showToDate = moment('01-11-2023', 'DD-MM-YYYY')
  const showCampaign = moment().isBetween(showFromDate, showToDate)

  return showCampaign
}

export const isNewCountry = (country = 'nl') => {
  return NewCountries.includes(country)
}

export const removeUnderscoresAndCamelCase = (str) => {
  return str.replace(/_([a-z])/g, function (_, letter) {
    return letter.toUpperCase()
  })
}

export const isValidSVGIcon = (svg = '') => icons.includes(svg)

export const getCurrencyIcon = (currency = 'euro') => {
  let icon = '€'

  switch (currency) {
    case 'euro':
      icon = '€'
      break
    case 'gbp':
      icon = '£'
      break
    case 'pln':
      icon = 'zł'
      break
    case 'chf':
      icon = 'Fr.'
      break
    case 'nok':
      icon = 'kr'
      break
    case 'czk':
      icon = 'Kč'
      break
    case 'dkk':
    case 'sek':
      icon = 'kr.'
  }

  return icon
}

export const stopShowingLookbookVideos = () => {
  const stopDate = new Date(2024, 0, 31)

  const now = new Date()

  return now > stopDate
}

export const extractValueBetweenBrackets = (str) => {
  const match = str.match(/\((.*?)\)/)

  return match ? match[1] : null
}

// Seeded random number generator for consistent SSR/client results
class SeededRandom {
  constructor(seed) {
    this.seed = seed
  }

  next() {
    this.seed = (this.seed * 9301 + 49297) % 233280
    return this.seed / 233280
  }
}

export const getRandomIndexes = (array, count, seed = null) => {
  if (array.length === 0) {
    return [0, 0, 0]
  }

  if (count > array.length) {
    return [0, 0, 0]
  }

  const indexes = []

  // Use seeded random if seed is provided, otherwise fall back to Math.random()
  const random = seed !== null ? new SeededRandom(seed) : null

  while (indexes.length < count) {
    const randomValue = random ? random.next() : Math.random()
    const index = Math.floor(randomValue * array.length)
    if (!indexes.includes(index)) {
      indexes.push(index)
    }
  }

  return indexes
}

export const splitIfExists = (str, splitter) => {
  if (str.includes(splitter)) {
    return str.split(splitter)[0]
  }

  return str
}

export const stripWebsiteAndLocaleAndCountry = (url) => {
  // Match the locale pattern and everything before it
  const regex = /^(?:https?:\/\/)?(?:[^/]+)?\/[a-z]{2}-[a-z]{2}/i

  // Replace the matched pattern with empty string and ensure starts with /
  const strippedPath = url.replace(regex, '')

  // If the path is empty, return /, otherwise return the stripped path
  return strippedPath || '/'
}
